import { useState } from 'react';
import { IAllTasksFilters } from '../types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDrawerStore } from 'store/drawer.store.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useAllWorklistStore } from '@store';

const initialFilters: IAllTasksFilters = {
  search: '',
  programs: [],
  due_date: '',
  category: '',
  location_building_ids: [],
  priorities: ['Overdue', 'Urgent', 'High', 'Medium', 'Low'],
  sort_by: 'due_date',
  sort_order: 'ASC',
  page: 1,
  limit: 100,
  statuses: ['Pending', 'In progress'],
  team_id: '',
  assignee_id: '',
  partner_id: '',
  practice_id: '',
  last_updated_after: '',
  task_names: [],
  order_categories: [],
  order_types: [],
  provider_ids: [],
  appliedFilters: 0,
};

const completedInitialFilters: IAllTasksFilters = {
  search: '',
  programs: [],
  due_date: '',
  category: '',
  location_building_ids: [],
  priorities: ['Overdue', 'Urgent', 'High', 'Medium', 'Low'],
  sort_by: 'due_date',
  sort_order: 'ASC',
  page: 1,
  limit: 100,
  statuses: ['Completed'],
  completion_interval: 'month',
  team_id: '',
  assignee_id: '',
  partner_id: '',
  practice_id: '',
  last_updated_after: '',
  task_names: [],
  order_categories: [],
  order_types: [],
  provider_ids: [],
  appliedFilters: 0,
};

export const useAllWorklist = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const client = useQueryClient();

  const { setDrawerOpen, setTaskId, taskId } = useDrawerStore(state => state);
  const {
    providerIds,
    partnerId,
    clearStore: clearPersistedFilters,
  } = useAllWorklistStore();

  const handleBack = () => {
    navigate(-1);
  };

  const [tabIndex, setTabIndex] = useState('incomplete');
  const [isFiltersCardOpen, setIsFiltersCardOpen] = useState(false);

  const handleCloseFiltersCard = () => setIsFiltersCardOpen(false);
  const handleToggleFiltersCard = () => setIsFiltersCardOpen(prev => !prev);

  const [filters, setFilters] = useState<IAllTasksFilters>({
    ...initialFilters,
    search: location.state ? location.state : '',
    provider_ids: providerIds,
    partner_id: partnerId,
  });

  const [completedFilters, setCompletedFilters] = useState<IAllTasksFilters>({
    ...completedInitialFilters,
    search: location.state ? location.state : '',
    provider_ids: providerIds,
    partner_id: partnerId,
  });

  const handleCloseDrawer = () => {
    setTaskId('');
    setDrawerOpen(false);
  };

  const handleOpenTaskDetails = (id: string) => {
    setTaskId(id);
    setDrawerOpen(true);
    client.invalidateQueries({
      queryKey: ['task'],
    });
  };

  const handleFilterChange = (newFilters: Partial<IAllTasksFilters>) => {
    setFilters({ ...filters, page: 1, ...newFilters });
  };

  const handleCompletedFilterChange = (
    newFilters: Partial<IAllTasksFilters>,
  ) => {
    setCompletedFilters({ ...filters, page: 1, ...newFilters });
  };

  const handleChangeTabIndex = (
    event: React.SyntheticEvent,
    newValue: string,
  ) => {
    setTabIndex(newValue);
  };

  const handleFullResetCompletedFilters = () => {
    setCompletedFilters({
      ...completedInitialFilters,
    });
    setFilters(prev => ({ ...prev, partner_id: '', provider_ids: [] }));
    clearPersistedFilters();
  };

  const handleFullResetIncompleteFilters = () => {
    setFilters({
      ...initialFilters,
    });
    setCompletedFilters(prev => ({
      ...prev,
      partner_id: '',
      provider_ids: [],
    }));
    clearPersistedFilters();
  };

  return {
    filters,
    handleFilterChange,
    isInitialFilters:
      JSON.stringify(filters) === JSON.stringify(initialFilters),
    selectedTaskId: taskId,
    handleCloseDrawer,
    handleOpenTaskDetails,
    handleBack,
    appliedFiltersCount: filters.appliedFilters || 0,
    handleChangeTabIndex,
    completedFilters,
    handleCompletedFilterChange,
    tabIndex,
    handleFullResetCompletedFilters,
    handleFullResetIncompleteFilters,
    isFiltersCardOpen,
    handleCloseFiltersCard,
    handleToggleFiltersCard,
  };
};
